import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Scale, ArrowLeft, Folder, Plus, Search, Clock,
  FileText, MessageSquare, Users, MoreVertical
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AuthContext } from "@/App";

// Use relative URL in production (deployed), or env variable in development
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PRACTICE_AREAS = [
  { value: "general", label: "General" },
  { value: "litigation", label: "Litigation" },
  { value: "corporate", label: "Corporate / M&A" },
  { value: "employment", label: "Employment" },
  { value: "property", label: "Real Estate / Property" },
  { value: "ip", label: "Intellectual Property" },
  { value: "finance", label: "Banking & Finance" },
  { value: "regulatory", label: "Regulatory" },
  { value: "tax", label: "Tax" },
];

const Matters = () => {
  const navigate = useNavigate();
  const { matterId } = useParams();
  const { user } = useContext(AuthContext);
  
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [matters, setMatters] = useState([]);
  const [selectedMatter, setSelectedMatter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  
  // Create matter form
  const [newMatter, setNewMatter] = useState({
    title: "",
    description: "",
    client_name: "",
    practice_area: "general"
  });

  useEffect(() => {
    fetchWorkspaces();
  }, []);

  useEffect(() => {
    if (selectedWorkspace) {
      fetchMatters(selectedWorkspace.workspace_id);
    }
  }, [selectedWorkspace]);

  useEffect(() => {
    if (matterId && matters.length > 0) {
      const matter = matters.find(m => m.matter_id === matterId);
      if (matter) setSelectedMatter(matter);
    }
  }, [matterId, matters]);

  const fetchWorkspaces = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/workspaces`);
      setWorkspaces(res.data);
      if (res.data.length > 0) {
        setSelectedWorkspace(res.data[0]);
      }
    } catch (error) {
      console.error("Error fetching workspaces:", error);
      toast.error("Failed to load workspaces");
    } finally {
      setLoading(false);
    }
  };

  const fetchMatters = async (workspaceId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/workspaces/${workspaceId}/matters`);
      setMatters(res.data);
    } catch (error) {
      console.error("Error fetching matters:", error);
    }
  };

  const handleCreateMatter = async () => {
    if (!newMatter.title.trim()) {
      toast.error("Please enter a matter title");
      return;
    }

    try {
      const res = await axios.post(`${BACKEND_URL}/api/matters`, {
        ...newMatter,
        workspace_id: selectedWorkspace.workspace_id
      });
      setMatters(prev => [res.data, ...prev]);
      toast.success("Matter created successfully");
      setCreateDialogOpen(false);
      setNewMatter({ title: "", description: "", client_name: "", practice_area: "general" });
    } catch (error) {
      console.error("Error creating matter:", error);
      toast.error("Failed to create matter");
    }
  };

  const filteredMatters = matters.filter(m => 
    m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.client_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C10]" data-testid="matters-page">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/dashboard")}
              data-testid="back-btn"
              className="text-[#94A3B8] hover:text-[#E2E8F0]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Scale className="w-6 h-6 text-[#C5A059]" />
              <span className="font-playfair text-lg font-semibold text-[#E2E8F0]">Matters</span>
            </div>
          </div>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="create-matter-btn"
                className="bg-[#C5A059] text-[#0B0C10] hover:bg-[#D4AF37] rounded-sm uppercase text-xs tracking-wide"
              >
                <Plus className="w-4 h-4 mr-2" /> New Matter
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#15171C] border-white/10">
              <DialogHeader>
                <DialogTitle className="font-playfair text-[#E2E8F0]">Create New Matter</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-[#94A3B8] font-mono text-xs uppercase">Title *</Label>
                  <Input 
                    value={newMatter.title}
                    onChange={(e) => setNewMatter(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Project Alpha Acquisition"
                    data-testid="matter-title-input"
                    className="mt-2 bg-[#0B0C10] border-white/10 text-[#E2E8F0]"
                  />
                </div>
                <div>
                  <Label className="text-[#94A3B8] font-mono text-xs uppercase">Client Name</Label>
                  <Input 
                    value={newMatter.client_name}
                    onChange={(e) => setNewMatter(prev => ({ ...prev, client_name: e.target.value }))}
                    placeholder="e.g., Acme Corporation Ltd"
                    data-testid="matter-client-input"
                    className="mt-2 bg-[#0B0C10] border-white/10 text-[#E2E8F0]"
                  />
                </div>
                <div>
                  <Label className="text-[#94A3B8] font-mono text-xs uppercase">Practice Area</Label>
                  <Select 
                    value={newMatter.practice_area} 
                    onValueChange={(v) => setNewMatter(prev => ({ ...prev, practice_area: v }))}
                  >
                    <SelectTrigger className="mt-2 bg-[#0B0C10] border-white/10 text-[#E2E8F0]" data-testid="practice-area-select">
                      <SelectValue placeholder="Select area" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#15171C] border-white/10">
                      {PRACTICE_AREAS.map(area => (
                        <SelectItem key={area.value} value={area.value} className="text-[#E2E8F0]">
                          {area.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#94A3B8] font-mono text-xs uppercase">Description</Label>
                  <Textarea 
                    value={newMatter.description}
                    onChange={(e) => setNewMatter(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of the matter..."
                    data-testid="matter-description-input"
                    className="mt-2 bg-[#0B0C10] border-white/10 text-[#E2E8F0] resize-none"
                    rows={3}
                  />
                </div>
                <Button 
                  onClick={handleCreateMatter}
                  data-testid="confirm-create-matter-btn"
                  className="w-full bg-[#C5A059] text-[#0B0C10] hover:bg-[#D4AF37] rounded-sm uppercase text-xs tracking-wide"
                >
                  Create Matter
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="flex h-[calc(100vh-65px)]">
        {/* Left Panel - Matter List */}
        <div className="w-80 border-r border-white/10">
          <div className="p-4 border-b border-white/10">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
              <Input 
                placeholder="Search matters..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-matters"
                className="pl-10 bg-[#15171C] border-white/10 text-[#E2E8F0] placeholder:text-[#64748B] rounded-sm"
              />
            </div>
            
            {workspaces.length > 1 && (
              <Select 
                value={selectedWorkspace?.workspace_id} 
                onValueChange={(v) => setSelectedWorkspace(workspaces.find(w => w.workspace_id === v))}
              >
                <SelectTrigger className="mt-3 bg-[#15171C] border-white/10 text-[#E2E8F0]">
                  <Folder className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Select workspace" />
                </SelectTrigger>
                <SelectContent className="bg-[#15171C] border-white/10">
                  {workspaces.map(ws => (
                    <SelectItem key={ws.workspace_id} value={ws.workspace_id} className="text-[#E2E8F0]">
                      {ws.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          
          <ScrollArea className="h-[calc(100vh-185px)]">
            <div className="p-2">
              {filteredMatters.length === 0 ? (
                <div className="text-center py-8">
                  <Folder className="w-10 h-10 text-[#64748B] mx-auto mb-3" />
                  <p className="text-[#94A3B8] font-manrope text-sm">No matters found</p>
                </div>
              ) : (
                filteredMatters.map((matter) => (
                  <button
                    key={matter.matter_id}
                    onClick={() => {
                      setSelectedMatter(matter);
                      navigate(`/matters/${matter.matter_id}`);
                    }}
                    data-testid={`matter-${matter.matter_id}`}
                    className={`w-full text-left p-3 rounded-sm mb-1 transition-colors ${
                      selectedMatter?.matter_id === matter.matter_id
                        ? 'bg-[#C5A059]/10 border border-[#C5A059]/30'
                        : 'hover:bg-white/5 border border-transparent'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-[#E2E8F0] font-manrope text-sm font-medium truncate">
                          {matter.title}
                        </p>
                        {matter.client_name && (
                          <p className="text-[#64748B] font-mono text-xs mt-1 truncate">
                            {matter.client_name}
                          </p>
                        )}
                      </div>
                      <Badge 
                        variant="outline" 
                        className={`ml-2 text-xs font-mono shrink-0 ${
                          matter.status === 'active' 
                            ? 'text-[#10B981] border-[#10B981]/30' 
                            : 'text-[#64748B] border-[#64748B]/30'
                        }`}
                      >
                        {matter.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-[#C5A059] font-mono text-xs uppercase bg-[#C5A059]/10 px-1.5 py-0.5 rounded-sm">
                        {matter.practice_area}
                      </span>
                      <span className="text-[#64748B] font-mono text-xs flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {new Date(matter.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Right Panel - Matter Details */}
        <div className="flex-1 bg-[#15171C]">
          {selectedMatter ? (
            <div className="h-full flex flex-col">
              <div className="px-6 py-4 border-b border-white/10">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="font-playfair text-2xl text-[#E2E8F0]">{selectedMatter.title}</h2>
                    {selectedMatter.client_name && (
                      <p className="text-[#94A3B8] font-manrope mt-1">{selectedMatter.client_name}</p>
                    )}
                  </div>
                  <Badge 
                    className={`${
                      selectedMatter.status === 'active' 
                        ? 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30' 
                        : 'bg-[#64748B]/10 text-[#64748B] border-[#64748B]/30'
                    } font-mono text-xs uppercase`}
                  >
                    {selectedMatter.status}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 mt-4">
                  <Badge variant="outline" className="text-[#C5A059] border-[#C5A059]/30 font-mono text-xs">
                    {PRACTICE_AREAS.find(a => a.value === selectedMatter.practice_area)?.label || selectedMatter.practice_area}
                  </Badge>
                  <span className="text-[#64748B] font-mono text-xs flex items-center">
                    <Clock className="w-3 h-3 mr-1" />
                    Created {new Date(selectedMatter.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="flex-1 p-6">
                {selectedMatter.description && (
                  <div className="mb-6">
                    <h3 className="font-playfair text-lg text-[#E2E8F0] mb-2">Description</h3>
                    <p className="text-[#94A3B8] font-manrope text-sm leading-relaxed">
                      {selectedMatter.description}
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <Card 
                    className="bg-[#0B0C10] border-white/5 rounded-sm p-4 hover:border-[#C5A059]/30 transition-colors cursor-pointer"
                    onClick={() => navigate("/chat", { state: { matterId: selectedMatter.matter_id, workspaceId: selectedMatter.workspace_id } })}
                    data-testid="matter-start-research"
                  >
                    <MessageSquare className="w-5 h-5 text-[#C5A059] mb-3" />
                    <h4 className="text-[#E2E8F0] font-manrope text-sm font-medium">Start Research</h4>
                    <p className="text-[#64748B] font-mono text-xs mt-1">Begin AI-assisted legal research</p>
                  </Card>
                  
                  <Card 
                    className="bg-[#0B0C10] border-white/5 rounded-sm p-4 hover:border-[#C5A059]/30 transition-colors cursor-pointer"
                    onClick={() => navigate("/documents", { state: { matterId: selectedMatter.matter_id } })}
                    data-testid="matter-view-documents"
                  >
                    <FileText className="w-5 h-5 text-[#C5A059] mb-3" />
                    <h4 className="text-[#E2E8F0] font-manrope text-sm font-medium">Documents</h4>
                    <p className="text-[#64748B] font-mono text-xs mt-1">View and upload matter documents</p>
                  </Card>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <Folder className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
                <h3 className="font-playfair text-xl text-[#E2E8F0] mb-2">Select a Matter</h3>
                <p className="text-[#94A3B8] font-manrope text-sm">
                  Choose a matter from the list or create a new one
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Matters;
